import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeRouteStopLook = 'route-stop-open' | 'route-stop-nav' | 'route-stop-call' | 'route-stop-move';

/* Released route planner stop row (Drive, "Plan my day"): four controls. The open button is the stop name: bare, left-aligned, in the label
   ink, on the `font` shorthand 600 15px/1.3 with the Wedge font, as the source writes it. Navigate, Call and Move are 40px by 34px boxes with an
   8px radius that sit 4px apart in one row. Navigate is the primary action: the soft accent fill, a 1px accent border and the accent ink.
   Call is the one control that dials a customer, so it takes the purple ink on the elevated fill with a 1px card border, as an anchor with no
   underline. Move (earlier and later) is the elevated fill with the same border, the secondary ink and a 15px arrow; held off (the first stop
   cannot move up, the last cannot move down) it takes the default cursor and 35% opacity. The Navigate, Call and Move boxes print no font family,
   as the source does. Each reads the exact legacy roles, so the shipped light ink rules keep matching. The caller owns the glyphs and the labels. */
const BOX_WIDTH = 40, BOX_HEIGHT = 34, BOX_RADIUS = 8, ARROW_SIZE = 15, HELD_OFF_OPACITY = 0.35;
const BOX = {width: BOX_WIDTH, height: BOX_HEIGHT, borderRadius: BOX_RADIUS} as const;

export function wedgeRouteStopStyle(look: WedgeRouteStopLook, disabled = false): CSSProperties {
  if (look === 'route-stop-open') return {
    background: 'none', border: 0, padding: 0, font: `600 15px/1.3 ${wedgeExactRole('font')}`, color: wedgeExactRole('label'),
    cursor: 'pointer', textAlign: 'left',
  };
  if (look === 'route-stop-nav') return {
    background: wedgeExactRole('accentTint'), border: `1px solid ${wedgeExactRole('accent')}`, ...BOX, color: wedgeExactRole('accent'),
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  };
  if (look === 'route-stop-call') return {
    background: wedgeExactRole('bgElev'), border: `1px solid ${wedgeExactRole('cardBorder')}`, ...BOX, color: wedgeExactRole('purple'),
    display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', boxSizing: 'border-box',
  };
  return {
    background: wedgeExactRole('bgElev'), border: `1px solid ${wedgeExactRole('cardBorder')}`, ...BOX, color: wedgeExactRole('labelSec'),
    fontSize: ARROW_SIZE, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? HELD_OFF_OPACITY : 1,
  };
}
